import { useState } from "react";
import styled from "styled-components";
import { productTranslations } from "../../locales/productTranslations";

/** Motifect dark theme `colors.primary` (header logo) — see motifect-ai `src/styles/themes.js` */
const MOTIFECT_PRIMARY = "#3B82F6";
/** Motifect dark theme `colors.textSecondary` */
const MOTIFECT_TEXT_SECONDARY = "#A1A1AA";

const MOTIFECT_SITE = "https://motifect.io";
const DEMO_VIDEO_URL =
  "https://d3is8g3qbv0vuy.cloudfront.net/site-assets/videos/motifect-demo.mp4";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 28px 20px 72px;
  width: 100%;
  max-width: 920px;
  margin: 0 auto;
`;

const typeFontStack =
  '"Inter", "Noto Sans KR", "Noto Sans JP", "Noto Sans SC", "Noto Sans", sans-serif';

const TypeRoot = styled.div`
  width: 100%;
  max-width: 680px;
  text-align: center;
  font-family: ${typeFontStack};
  margin-bottom: 22px;
`;

const TitleHeading = styled.h2`
  margin: 0 0 14px;
  padding: 0;
  border: 0;
  font: inherit;
`;

const WordmarkLink = styled.a`
  display: inline-block;
  font-family: "Silkscreen", "Inter", sans-serif;
  font-size: clamp(2.5rem, 8vw, 3.75rem);
  font-weight: 400;
  line-height: 1.05;
  letter-spacing: 0.04em;
  color: ${MOTIFECT_PRIMARY};
  text-decoration: none;
  cursor: pointer;

  &:hover,
  &:active {
    color: ${MOTIFECT_PRIMARY};
  }

  &:focus-visible {
    outline: 2px solid ${MOTIFECT_PRIMARY};
    outline-offset: 6px;
    border-radius: 4px;
  }
`;

const Tagline = styled.p`
  margin: 0;
  font-size: clamp(0.9rem, 1.65vw, 1rem);
  font-weight: 500;
  line-height: 1.55;
  color: ${MOTIFECT_TEXT_SECONDARY};
  max-width: 520px;
  margin-left: auto;
  margin-right: auto;
`;

const MediaFrame = styled.div`
  width: 100%;
  max-width: 900px;
  border-radius: 14px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.55);
  position: relative;
  aspect-ratio: 1364 / 912;
`;

const DemoVideo = styled.video`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center top;
  opacity: ${(props) => (props.$ready ? 1 : 0)};
  transition: opacity 0.35s ease;

  &::-webkit-media-controls {
    display: none !important;
  }

  &::-webkit-media-controls-enclosure {
    display: none !important;
  }
`;

const Spinner = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 1;

  &::after {
    content: "";
    width: 36px;
    height: 36px;
    border: 3px solid rgba(255, 255, 255, 0.12);
    border-top-color: rgba(255, 255, 255, 0.82);
    border-radius: 50%;
    animation: spin 0.85s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

function ProductTab({ currentLang }) {
  const { wordmark, tagline, videoLabel } = productTranslations[currentLang];
  const [videoReady, setVideoReady] = useState(false);

  return (
    <Container>
      <TypeRoot>
        <TitleHeading>
          <WordmarkLink
            href={MOTIFECT_SITE}
            target="_blank"
            rel="noopener noreferrer"
          >
            {wordmark}
          </WordmarkLink>
        </TitleHeading>
        <Tagline>{tagline}</Tagline>
      </TypeRoot>

      <MediaFrame>
        {!videoReady && <Spinner />}
        <DemoVideo
          src={DEMO_VIDEO_URL}
          playsInline
          muted
          loop
          autoPlay
          preload="auto"
          $ready={videoReady}
          onLoadedData={() => setVideoReady(true)}
          onError={() => setVideoReady(true)}
          aria-label={videoLabel}
          disablePictureInPicture
        />
      </MediaFrame>
    </Container>
  );
}

export default ProductTab;
